export const BREADCRUMB_RE = /^\[\[[^\]|]*(?:\|[^\]]+)?\]\][^\n]* \/ [^\n]*\n?/

export function stripFrontmatter(raw: string): string {
  return raw.replace(/^---\n[\s\S]*?\n---\n?/, '')
}

export interface WikiConcept {
  name: string     // canonical concept page name (last path segment of target)
  target: string   // raw [[target]] before the | alias
  excerpt?: string // cleaned text of the syllabus line that mentions this concept
}

export interface WikiTopic {
  name: string
  weight?: string  // e.g. "23-30%"
  concepts: WikiConcept[]
}

export interface WikiResource {
  name: string     // display name, e.g. "A First Course in Probability (Ross - 2019)"
  target: string   // raw [[target]] (path or name) used to build the wiki route
}

export interface WikiExamSyllabus {
  examId: string
  examLabel: string
  examTopic: string
  topics: WikiTopic[]
  /** Source-material books, parsed from the `[!answer]` callout. */
  resources: WikiResource[]
  /** Source file name without .md extension, e.g. "Exam FM-2 (SOA)" */
  fileName?: string
}

// Maps a wikiParser examId to the exam_progress table key used in tracks.ts.
// "P-1" → "P", "FM-2" → "FM", "MAS-I" → "MAS-I", "7" → "CAS-7", "6C" → "CAS-6",
// "PCPA" → "CAS-PCPA", "DISC-DA" → "CAS-DA"
export function wikiExamIdToProgressKey(examId: string): string {
  if (examId.startsWith('MAS-')) return examId
  // PCPA is a CAS requirement named by letters rather than a number, and the
  // tracks key it `CAS-PCPA` — the bare-letters branch below would give `PCPA`.
  if (examId === 'PCPA') return 'CAS-PCPA'
  // The three DISC courses: the tracks key them by their letters alone
  // (`CAS-DA`, `CAS-RM`, `CAS-IA`).
  const disc = examId.match(/^DISC-([A-Z]+)$/)
  if (disc) return `CAS-${disc[1]}`
  // The three VEEs keep their own ids (`VEE-MS`, `VEE-ECON`, `VEE-AF`) — the
  // CAS-number branch at the end would read them as `CAS-VEE-…`.
  if (/^VEE-[A-Z]+$/.test(examId)) return examId
  // The FSA's two required courses: the tracks key them `FSA-DMAC`, `FSA-FAC`.
  if (examId === 'DMAC' || examId === 'FAC') return `FSA-${examId}`
  // The CAS's Course on Professionalism has always been tracked as `CAS-APC`
  // (it is the CAS's counterpart of the SOA's APC); the key is kept, since
  // readers' progress is stored under it.
  if (examId === 'COP') return 'CAS-APC'
  // The SOA's fellowship (FSA) exams: `GH-101`, `GH-201C`, `ILA-201U`. The
  // tracks key them `FSA-GH101`, one key for a course's regional variants (the
  // way Exam 6's variants share `CAS-6`) — so the `-C`/`-U` letter is dropped.
  // The CIA's own requirements: the FCIA track exams (`F1PC`, `F3BNK`) and
  // the ACIA and FCIA track modules (`ACIA-M1`, `PC-M2`) and the ACIA
  // Capstone Exam (`ACIA-CAPSTONE`), each keyed under the
  // body — `CIA-F1PC`, `CIA-PC-M2` — so none can collide with an SOA or CAS key.
  if (/^F[123](?:PC|ILA|GB|RET|FIE|BNK)$/.test(examId) || /^(?:ACIA|PC|ILA|GB|RET|FIE)-M[12]$/.test(examId)
    || examId === 'ACIA-CAPSTONE') {
    return `CIA-${examId}`
  }
  const fsa = examId.match(/^(CFE|CP|GH|GI|ILA|INV|RET)-(\d{3})[A-Z]?$/)
  if (fsa) return `FSA-${fsa[1]}${fsa[2]}`
  if (/^[A-Z]+-\d+$/.test(examId)) return examId.replace(/-\d+$/, '')
  if (/^[A-Z]+$/.test(examId)) return examId
  return 'CAS-' + examId.replace(/[A-Z]+$/, '')
}

// Strip [[wiki links]] from text, keeping only display names, for use as excerpts.
export function cleanWikiLinks(text: string): string {
  return text
    .replace(/\[\[[^\]|]+\|([^\]]+)\]\]/g, '$1')
    .replace(/\[\[([^\]]+)\]\]/g, (_, t) =>
      t.includes('/') ? (t.split('/').pop() ?? t) : t)
}

// Extract all [[Name]] and [[Name|Display]] patterns from text.
// Always uses the last path segment of the target as the concept name so that
// aliased links like [[Callable Bond|Callable]] produce name="Callable Bond",
// not the syllabus shorthand "Callable". Deduplicates by lowercased name.
function extractWikiLinks(text: string): WikiConcept[] {
  const regex = /\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g
  const seen = new Set<string>()
  const concepts: WikiConcept[] = []
  let match: RegExpExecArray | null

  while ((match = regex.exec(text)) !== null) {
    const target = match[1].trim()
    const baseName = target.includes('/') ? target.split('/').pop()! : target
    const name = baseName

    if (!seen.has(name.toLowerCase())) {
      seen.add(name.toLowerCase())
      concepts.push({ name, target })
    }
  }

  return concepts
}

// Extract exam identity from the data-current attribute on the exam-nav div.
// data-current="P-1|Probability" → { examId: "P-1", examTopic: "Probability", examLabel: "Exam P" }
export function parseExamMetadata(
  content: string,
): { examId: string; examTopic: string; examLabel: string } | null {
  const match = content.match(/data-current="([^|"]+)\|([^"]+)"/)
  if (!match) return null
  const examId = match[1].trim()        // e.g. "P-1"
  const examTopic = match[2].trim()     // e.g. "Probability"
  const examLabel = 'Exam ' + examId.replace(/-\d+$/, '')  // "Exam P"
  return { examId, examTopic, examLabel }
}

// Parse an exam markdown file and extract Learning Objectives as a
// WikiExamSyllabus. `[!example]` callouts (`> [!example]- TopicName {weight}`)
// become topics; every [[wiki link]] inside becomes a concept. Any other
// callout ends the current topic; the book links inside the
// `[!answer]- Source Material` block are collected as resources so they don't
// get folded into the last learning objective. Callouts of any other type
// (e.g. `[!info]`/`[!tip]` exam-day and study-approach notes) are ignored
// entirely — their wiki links are guidance, not syllabus concepts or sources.
export function parseExamSyllabus(
  content: string,
  examId: string,
  examLabel: string,
  examTopic: string,
  fileName?: string,
): WikiExamSyllabus {
  const lines = content.split('\n')
  const topics: WikiTopic[] = []

  interface Acc { name: string; weight?: string; lines: string[] }
  let current: Acc | null = null
  let inResource = false
  const resourceLines: string[] = []

  const flush = () => {
    if (!current) return
    const concepts: WikiConcept[] = []
    const seen = new Set<string>()
    for (const line of current.lines) {
      const lineLinks = extractWikiLinks(line)
      const excerpt = cleanWikiLinks(line).trim()
      for (const link of lineLinks) {
        if (!seen.has(link.name.toLowerCase())) {
          seen.add(link.name.toLowerCase())
          concepts.push({ ...link, excerpt })
        }
      }
    }
    topics.push({ name: current.name, weight: current.weight, concepts })
    current = null
  }

  for (const line of lines) {
    // Any callout header: > [!type]- Title {weight}
    const header = line.match(/^>\s*\[!(\w+)\]-?\s*([^{}\n]*?)(?:\s*\{([^}]+)\})?\s*$/)
    if (header) {
      flush()
      const kind = header[1].toLowerCase()
      if (kind === 'example') {
        inResource = false
        current = { name: header[2].trim(), weight: header[3]?.trim(), lines: [] }
      } else {
        // Only the `[!answer]- Source Material` callout holds resources. Other
        // callout types (exam-day notes, study advice) end the current topic but
        // contribute neither concepts nor resources — their links are guidance,
        // not syllabus entries.
        inResource = kind === 'answer'
      }
    } else if (line.startsWith('>')) {
      const body = line.replace(/^>\s?/, '')
      if (current) current.lines.push(body)
      else if (inResource) resourceLines.push(body)
    }
    // Blank lines and non-callout lines outside a block are ignored
  }

  flush()

  const resources: WikiResource[] = []
  const seenResources = new Set<string>()
  for (const line of resourceLines) {
    for (const link of extractWikiLinks(line)) {
      if (!seenResources.has(link.name.toLowerCase())) {
        seenResources.add(link.name.toLowerCase())
        resources.push({ name: link.name, target: link.target })
      }
    }
  }

  return { examId, examLabel, examTopic, topics, resources, fileName }
}

/**
 * All syllabi whose topic lists reference a concept, matched by display name
 * or by the raw `[[target]]` basename (handles `[[Bond Price|Price]]` aliases).
 * A concept taught in more than one exam's study guide yields multiple results —
 * callers must not silently pick the first one; ask the user which to open.
 */
export function findSyllabiForConcept(
  syllabi: WikiExamSyllabus[],
  conceptName: string,
): WikiExamSyllabus[] {
  const needle = conceptName.toLowerCase()
  return syllabi.filter(s => s.topics.some(t => t.concepts.some(c => {
    if (c.name.toLowerCase() === needle) return true
    const targetBase = c.target.split('/').pop()?.replace(/\.md$/i, '').toLowerCase()
    return targetBase === needle
  })))
}
